import React, { useEffect } from 'react';
import { useForm, Controller } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import UsersDrawer from '../UsersDrawer';
import {
  Button,
  FormControl,
  TextField,
  CircularProgress,
} from '@material-ui/core';
import { FlexBox } from '../../../global/FlexBox';
import { useUpdateOrganization } from '../../hooks';
import { EDIT_ORG_DRAWER_ID, INPUT_MAX_LENGTH } from '../../constants';
import { useMinimizedDrawers } from '../../context/MinimizedDrawersContext';
import { useUserManagementContext } from '../../context/UserManagementContext';
import { enqueueSnackbar } from 'notistack';
import { Organization } from '@app/shared/types';
import {
  organizationValidationSchema,
  OrganizationFormData,
} from '../../validationSchemas';
import { getApiErrorMessage } from '../../../../shared/api/getApiErrorMessage';
import { TeamAdminsField } from './TeamAdmins/TeamAdminsField';

const DEFAULT_FORM_DATA: OrganizationFormData = {
  name: '',
  description: '',
  orgAdminEmails: [],
};

export const EditOrgDrawer = () => {
  const {
    isEditOrganisationsDrawerOpen,
    setIsEditOrganisationsDrawerOpen,
    currentOrgData,
    setCurrentOrgData,
  } = useUserManagementContext();

  const {
    mutate: updateOrg,
    isPending,
    reset: resetMutation,
  } = useUpdateOrganization();

  const { minimizeDrawer } = useMinimizedDrawers();

  const { control, handleSubmit, setValue, getValues, reset, clearErrors } =
    useForm<OrganizationFormData>({
      resolver: yupResolver(organizationValidationSchema),
      defaultValues: DEFAULT_FORM_DATA,
      mode: 'onSubmit',
    });

  const resetForm = () => {
    reset();
    resetMutation();
  };

  const handleDrawerClose = () => {
    resetForm();
    setCurrentOrgData(null);
    setIsEditOrganisationsDrawerOpen(false);
  };

  const onSubmit = async (data: OrganizationFormData) => {
    if (!currentOrgData?.id) return;

    clearErrors();

    const orgToUpdate: Organization = {
      ...data,
      id: currentOrgData.id,
    };

    updateOrg(orgToUpdate, {
      onSuccess: () => {
        resetForm();
        setCurrentOrgData(null);
        setIsEditOrganisationsDrawerOpen(false);
        enqueueSnackbar('Team updated successfully', {
          variant: 'success',
        });
      },
      onError: error => {
        enqueueSnackbar(getApiErrorMessage(error, 'Failed to edit team'), {
          variant: 'error',
        });
      },
    });
  };

  const handleEditOrg = () => {
    handleSubmit(onSubmit)();
  };

  useEffect(() => {
    if (currentOrgData) {
      setValue('name', currentOrgData.name);
      setValue('description', currentOrgData.description || '');
      setValue('orgAdminEmails', currentOrgData.orgAdminEmails || []);
    }
  }, [currentOrgData, setValue]);

  const handleHide = () => {
    const formData = getValues();
    minimizeDrawer(EDIT_ORG_DRAWER_ID, 'Edit Team', formData);
    handleDrawerClose();
  };

  return (
    <UsersDrawer
      id={EDIT_ORG_DRAWER_ID}
      title="Edit Team"
      hasHide
      handleDrawerClose={handleDrawerClose}
      isOpen={isEditOrganisationsDrawerOpen}
      confirmOnClose
      confirmTitle="Are you sure you want to cancel?"
      confirmContent="All the details provided will be lost."
      confirmButtonText="Cancel Editing Team"
      cancelButtonText="Dismiss"
      handleHide={handleHide}
    >
      <FormControl
        style={{
          display: 'flex',
          width: '100%',
          gap: 32,
          flexDirection: 'column',
          marginBottom: 24,
        }}
      >
        <Controller
          name="name"
          control={control}
          render={({ field, fieldState: { error } }) => (
            <TextField
              {...field}
              variant="outlined"
              placeholder="Team Name"
              label="Team Name"
              InputLabelProps={{ shrink: true }}
              error={!!error}
              helperText={error?.message}
              disabled={isPending}
              inputProps={{
                maxLength: INPUT_MAX_LENGTH,
              }}
              required
            />
          )}
        />

        <Controller
          name="description"
          control={control}
          render={({ field, fieldState: { error } }) => (
            <TextField
              {...field}
              variant="outlined"
              placeholder="Team Description"
              label="Description"
              InputLabelProps={{ shrink: true }}
              error={!!error}
              helperText={error?.message}
              disabled={isPending}
              inputProps={{
                maxLength: INPUT_MAX_LENGTH,
              }}
            />
          )}
        />

        <Controller
          name="orgAdminEmails"
          control={control}
          render={({ field, fieldState: { error } }) => (
            <TeamAdminsField
              value={field.value || []}
              onChange={field.onChange}
              disabled={isPending}
              error={!!error}
              helperText={error?.message}
            />
          )}
        />
      </FormControl>
      <FlexBox sx={{ justifyContent: 'flex-end', gridColumnGap: 24 }}>
        <Button
          variant="contained"
          color="primary"
          onClick={handleEditOrg}
          disabled={isPending}
        >
          {isPending ? (
            <FlexBox sx={{ alignItems: 'center' }}>
              <CircularProgress
                size={20}
                color="inherit"
                style={{ marginRight: 8 }}
              />
              Saving...
            </FlexBox>
          ) : (
            'Save Changes'
          )}
        </Button>
      </FlexBox>
    </UsersDrawer>
  );
};
